
import React, { useState, useEffect, useRef } from "react";
import { Page, Grid, Card, Form, Button } from "tabler-react";
import { Modal } from "react-bootstrap";
import SiteWrapper from "../../SiteWrapper";

import * as productServices from "../../store/services/productServices";
import * as categoryServices from "../../store/services/categoryServices";
import * as tagServices from "../../store/services/tagServices";
import { BACKENDURL } from "../../store/services/type";
import { ROLE_ADMIN, ROLE_DEALER } from "../../store/types";

const generateUniqueId = () => {
    return Math.floor(10000 + Math.random() * 90000).toString();
};

function EditProduct(props) {

    const [user] = useState((localStorage.getItem("user") && JSON.parse(localStorage.getItem("user"))) || {});

    const [ loading, setLoading ] = useState(false);
    const [ id ] = useState( Number( props.match.params.id ) );
    const [ errors, setErrors ] = useState({});
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ handleModalShow, setHandleModalShow] = useState(false);

    const [ selectedFiles, setSelectedFiles] = useState([]);
    const [ savedImages, setSavedImages] = useState([]);
    const [ deletedImages, setDeletedImages] = useState([]);
    const [ files, setFiles] = useState([]);
    const [ measurements, setMeasurements ] = useState([]);
    const [ categories, setCategories ] = useState([]);
    const [ tags, setTags ] = useState([]);
    const [ measureUnit, setMeasureUnit ] = useState('in');
    const [ weightUnit, setWeightUnit ] = useState('stlb');

    // Variants: -1 = base product tab
    const [ activeVariant, setActiveVariant ] = useState(-1);
    const [ variants, setVariants ] = useState([]);
    // Per-variant image state: array of { savedImages, selectedFiles, deletedImages, files }
    const [ variantImageStates, setVariantImageStates ] = useState([]);

    const variantFileRefs = useRef([]);

    const cmToIn = (val) => val != null && val !== '' ? parseFloat((parseFloat(val) / 2.54).toFixed(2)) : '';
    const inToCm = (val) => val != null && val !== '' ? parseFloat((parseFloat(val) * 2.54).toFixed(2)) : '';
    const displayVal = (cmVal) => measureUnit === 'in' ? cmToIn(cmVal) : (cmVal ?? '');
    const storeVal = (inputVal) => measureUnit === 'in' ? (inputVal !== '' ? inToCm(inputVal) : '') : (inputVal ?? '');

    const kgToStLb = (kgVal) => {
        if (kgVal == null || kgVal === '') return { st: '0', lb: '0' };
        const kg = parseFloat(kgVal);
        const totalLb = Math.round(kg * 2.20462);
        const st = Math.floor(totalLb / 14);
        const lb = totalLb % 14;
        return { st: String(st), lb: String(lb) };
    };
    const stLbToKg = (st, lb) => {
        const s = parseFloat(st) || 0;
        const l = parseFloat(lb) || 0;
        return parseFloat(((s * 14 + l) / 2.20462).toFixed(2));
    };
    const fileInputRef = useRef(null);


    const [ formElements, setFormElements ] = useState({
        unique_id: generateUniqueId(),
        sku: '',
        status: 'published',
        product: '',
        price: '',
        dealer: '',
        category: 0,
        url: '',
        width: '',
        height: '',
        depth: '',
        weight: '',
        description: '',
        features: '',
        default_label: '',
    });


    useEffect(() => {
        categoryServices
            .getCategories()
            .then(( res ) => {
                const data = JSON.parse(res.data);
                setCategories(data.data);
                setLoading(false);
            })
            .catch(( err ) => {
                setLoading(false);
            });

        tagServices
            .getTags()
            .then((res) => {
                const data = JSON.parse(res.data);
                setTags(data.data || []);
            })
            .catch(() => {});

        if ( id > 0 ) {
            setLoading(true);
            productServices
                .getProduct(id)
                .then(( res ) => {
                    const data = JSON.parse(res.data);
                    
                    setFormElements({
                        unique_id: data?.data?.unique_id,
                        sku: data?.data?.sku || '',
                        status: data?.data?.status || 'published',
                        product: data?.data?.product,
                        price: data?.data?.price,
                        dealer: data?.data?.user_id,
                        category: data?.data?.category?.id,
                        url: data?.data?.url,
                        width: data?.data?.width,
                        height: data?.data?.height,
                        depth: data?.data?.depth,
                        weight: data?.data?.weight,
                        description: data?.data?.description,
                        features: data?.data?.features,
                        default_label: data?.data?.default_label || '',
                    });

                    setMeasurements(data?.data?.measurements || []);
                    setSavedImages(data?.data?.images || []);
                    const loadedVariants = data?.data?.variants || [];
                    setVariants(loadedVariants);
                    setVariantImageStates(loadedVariants.map(v => ({
                        savedImages: v.images || [],
                        selectedFiles: [],
                        deletedImages: [],
                        files: [],
                    })));

                    setLoading(false);
                })
                .catch(err => {
                    setLoading(false);
                })
        }
    }, [ id ])

    const saveProduct = () => {
        setLoading(true);
        setErrors({});

        var formData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('images[]', files[i]);
        }

        if(deletedImages.length > 0) {
            for (let i = 0; i < deletedImages.length; i++) {
                formData.append('deletedImages[]', deletedImages[i]);
            }
        } else {
            formData.append('deletedImages', []);
        }

        for (let i = 0; i < savedImages.length; i++) {
            formData.append('savedImages[]', savedImages[i]);
        }

        formData.append("measurements", JSON.stringify(measurements));

        // Variants metadata (without images — those are sent separately)
        const variantMeta = variants.map((v, i) => ({
            type: v.type,
            label: v.label,
            url: v.url || '',
            price: v.price || '',
        }));
        formData.append("variantData", JSON.stringify(variantMeta));

        // Per-variant images
        variants.forEach((_, i) => {
            const vs = variantImageStates[i] || {};
            (vs.savedImages || []).forEach(p => formData.append(`variantSavedImages[${i}][]`, p));
            (vs.deletedImages || []).forEach(p => formData.append(`variantDeletedImages[${i}][]`, p));
            (vs.files || []).forEach(f => formData.append(`variantImages[${i}][]`, f));
        });

        formData.append("id", id);

        for (const key in formElements) {
            formData.append(key, formElements[key]);
        }
        formData.append('user_id', user?.id);

        productServices
            .saveProduct( formData )
            .then((res) => {
                setSaveSuccess(true);

                setLoading(false);
            })
            .catch((err) => {
                setErrors(err?.response?.data?.errors || {});
                setLoading(false);
            });
    }

    const handleClose = () => {
        setHandleModalShow(false);
    }

    const deleteProduct = () => {
        productServices
            .deleteProduct(id)
            .then(res => {
                const data = JSON.parse(res.data);
                if(data?.message === "Success") {
                    props.history.push("/products");
                }
            })
            .catch(err => {})
    }

    const duplicateProduct = () => {
        setLoading(true);
        productServices
            .duplicateProduct(id)
            .then(res => {
                const data = JSON.parse(res.data);
                props.history.push(`/products/${data?.data?.id}`);
            })
            .catch(() => setLoading(false));
    }






    const addVariant = () => {
        const newVariant = { type: 'colour', label: '', url: '', price: '' };
        setVariants(prev => [...prev, newVariant]);
        setVariantImageStates(prev => [...prev, { savedImages: [], selectedFiles: [], deletedImages: [], files: [] }]);
        setActiveVariant(variants.length);
    };

    const removeVariant = (index) => {
        setVariants(prev => prev.filter((_, i) => i !== index));
        setVariantImageStates(prev => prev.filter((_, i) => i !== index));
        setActiveVariant(-1);
    };

    const updateVariant = (index, field, value) => {
        setVariants(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
    };

    const updateVariantImageState = (index, field, value) => {
        setVariantImageStates(prev => prev.map((vs, i) => i === index ? { ...vs, [field]: value } : vs));
    };

    const handleVariantFileInput = (index, e) => {
        if (!e.target?.files) return;
        const vs = variantImageStates[index] || {};
        const newFiles = [...(vs.files || [])];
        const newSelected = [...(vs.selectedFiles || [])];
        for (let i = 0; i < e.target.files.length; i++) {
            const file = e.target.files[i];
            newFiles.push(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                newSelected.push({ filename: file.name, filetype: file.type, fileimage: reader.result, filesize: filesizes(file.size) });
                updateVariantImageState(index, 'selectedFiles', [...newSelected]);
            };
            reader.readAsDataURL(file);
        }
        updateVariantImageState(index, 'files', newFiles);
    };

    const filesizes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    const InputChange = (e) => {
        if (e.target && e.target.files) {
            // --For Multiple File Input
            let images = [];
            for (let i = 0; i < e.target.files.length; i++) {
                images.push(e.target.files[i]);
                let reader = new FileReader();
                let file = e.target.files[i];
                setFiles((prev) => [...prev, file]);
                reader.onloadend = () => {
                    setSelectedFiles((preValue) => [
                        ...preValue,
                        {
                            // id: shortid.generate(),
                            filename: file.name,
                            filetype: file.type,
                            fileimage: reader.result,
                            // datetime: file.lastModifiedDate.toLocaleString('en-IN'),
                            filesize: filesizes(file.size)
                        }
                    ]);
                };
                if (file) {
                    reader.readAsDataURL(file);
                }
            }
        } else {
            console.error("File input target is null or undefined.");
        }
    };


    const DeleteSelectFile = (index) => {
        setSelectedFiles((prevFiles) => [
            ...prevFiles.slice(0, index),
            ...prevFiles.slice(index + 1)
        ]);
    }

    const DeleteSavedFile = (index) => {
        const newImages = savedImages.filter((image, i) => i === index);
        setDeletedImages((prev) => [...prev, ...newImages]);
        setSavedImages((prevFiles) => [
            ...prevFiles.slice(0, index),
            ...prevFiles.slice(index + 1)
        ]);
    }

    return (
        <SiteWrapper loading={loading}>
            <Page.Content>
                <div className="page-header d-flex justify-content-between">
                    <h1 className="page-title">
                        Products
                    </h1>
                </div>
                <Grid.Row>
                    <Grid.Col>
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <Card.Title style={{ margin: 0 }}>
                                    { id === 0 ? 'Add Product' : `Edit Product — ID ${String(id).padStart(4, '0')}` }
                                </Card.Title>

                                <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                    {/* Cancel */}
                                    <button type="button" onClick={() => props.history.push("/products")} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #dee2e6', borderRadius: 4, background: 'none', color: '#6c757d', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                                        Cancel
                                    </button>

                                    { id > 0 && (
                                        <>
                                            {/* Status toggle */}
                                            <button
                                                type="button"
                                                onClick={() => setFormElements({...formElements, status: formElements.status === 'published' ? 'draft' : 'published'})}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: `1px solid ${formElements.status === 'published' ? '#2B9A99' : '#dee2e6'}`, borderRadius: 4, background: 'none', color: formElements.status === 'published' ? '#2B9A99' : '#6c757d', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                            >
                                                { formElements.status === 'published' ? (
                                                    <svg width="16" height="11" viewBox="0 0 16 11" fill="none"><path d="M8 1C4.5 1 1.5 5.5 1.5 5.5S4.5 10 8 10s6.5-4.5 6.5-4.5S11.5 1 8 1z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
                                                ) : (
                                                    <svg width="16" height="13" viewBox="0 0 16 13" fill="none"><path d="M1 1L15 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M4 3.5C2.5 4.8 1.5 6.5 1.5 6.5S4.5 11 8 11c1 0 2-.3 2.8-.8M6 1.3C6.6 1.1 7.3 1 8 1c3.5 0 6.5 4.5 6.5 4.5s-.8 1.4-2.2 2.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M5.5 6.5a2.5 2.5 0 003.8 2.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                )}
                                                { formElements.status === 'published' ? 'Published' : 'Draft' }
                                            </button>

                                            {/* Duplicate */}
                                            <button type="button" onClick={duplicateProduct} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #dee2e6', borderRadius: 4, background: 'none', color: '#6c757d', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                <svg width="14" height="15" viewBox="0 0 14 15" fill="none"><rect x="1" y="4" width="9" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M4 4V2.5A1.5 1.5 0 015.5 1H12A1.5 1.5 0 0113.5 2.5V10A1.5 1.5 0 0112 11.5H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                Duplicate
                                            </button>

                                            {/* Delete */}
                                            <button type="button" onClick={() => setHandleModalShow(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #e74c3c', borderRadius: 4, background: 'none', color: '#e74c3c', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                <svg width="13" height="14" viewBox="0 0 13 14" fill="none"><path d="M1 3.5h11M4.5 3.5V2h4v1.5M2 3.5l.8 9h7.4l.8-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 6.5v3.5M8 6.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                                Delete
                                            </button>
                                        </>
                                    )}

                                    {/* Save */}
                                    <button type="button" onClick={() => saveProduct()} className="btn-mm" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 4, border: 'none', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        <svg width="14" height="11" viewBox="0 0 14 11" fill="none"><path d="M1 5.5L5.5 10L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        { id === 0 ? 'Add Product' : 'Save' }
                                    </button>
                                </div>
                            </Card.Header>

                            {/* Variant tab bar */}
                            { id > 0 && (
                                <div style={{ borderBottom: '1px solid #e9ecef', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <button
                                        type="button"
                                        onClick={() => setActiveVariant(-1)}
                                        style={{ padding: '10px 16px', border: 'none', background: 'none', borderBottom: activeVariant === -1 ? '2px solid #2B9A99' : '2px solid transparent', color: activeVariant === -1 ? '#2B9A99' : '#6c757d', fontWeight: activeVariant === -1 ? 600 : 400, fontSize: 13, cursor: 'pointer', marginBottom: -1 }}
                                    >{ formElements.default_label || 'Base' }</button>
                                    {variants.map((v, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <button
                                                type="button"
                                                onClick={() => setActiveVariant(i)}
                                                style={{ padding: '10px 16px', border: 'none', background: 'none', borderBottom: activeVariant === i ? '2px solid #2B9A99' : '2px solid transparent', color: activeVariant === i ? '#2B9A99' : '#6c757d', fontWeight: activeVariant === i ? 600 : 400, fontSize: 13, cursor: 'pointer', marginBottom: -1 }}
                                            >{ v.label || `Colour ${i + 1}` }</button>
                                            <button
                                                type="button"
                                                onClick={() => removeVariant(i)}
                                                style={{ border: 'none', background: 'none', color: '#aaa', fontSize: 14, cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
                                            >×</button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addVariant}
                                        style={{ padding: '10px 12px', border: 'none', background: 'none', color: '#2B9A99', fontSize: 13, cursor: 'pointer', marginBottom: -1 }}
                                    >+ Add Colour</button>
                                </div>
                            )}

                            <Card.Body >

                                <Grid.Row className="pb-5">
                                    {
                                        !saveSuccess &&
                                        <Grid.Col width={ 12 }>
                                            <Grid.Row>
                                                <Grid.Col xl={ 6 } width={ 12 } style={ activeVariant >= 0 ? { opacity: 0.35, pointerEvents: 'none', userSelect: 'none', position: 'relative' } : {} }>
                                                    { activeVariant >= 0 && (
                                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 24 }}>
                                                            <span style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: '6px 14px', fontSize: 12, color: '#6c757d', fontWeight: 500, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                                                                Shared across all variants
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Product Info */}
                                                    <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 4px', marginBottom: 16 }}>
                                                        <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Product Info</p>
                                                        <Grid.Row>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label="PRODUCT" >
                                                                    <Form.Input
                                                                        placeholder="Product Name"
                                                                        onChange={(e) => setFormElements({...formElements, product: e.target.value})}
                                                                        value={formElements.product}
                                                                        className="input-normal"
                                                                        feedback={errors?.product && errors?.product[0]}
                                                                        invalid={errors?.product}
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label="SKU (optional)" >
                                                                    <Form.Input
                                                                        placeholder="Your SKU reference"
                                                                        onChange={(e) => setFormElements({...formElements, sku: e.target.value})}
                                                                        value={formElements.sku}
                                                                        className="input-normal"
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label="PRICE" >
                                                                    <Form.Input
                                                                        placeholder="Price"
                                                                        onChange={(e) => setFormElements({...formElements, price: e.target.value})}
                                                                        value={formElements.price}
                                                                        className="input-normal"
                                                                        feedback={errors?.price && errors?.price[0]}
                                                                        invalid={errors?.price}
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label="CATEGORY" >
                                                                    <Form.Select
                                                                        onChange={(e) => setFormElements({...formElements, category: e.target.value})}
                                                                        value={formElements.category}
                                                                        className="input-normal"
                                                                        feedback={errors?.category && errors?.category[0]}
                                                                        invalid={errors?.category}
                                                                    >
                                                                        <option></option>
                                                                        {categories.map((category, index) => (
                                                                            <option value={ category?.id } key={ index }>{category?.category}</option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                            <Grid.Col width={ 12 } >
                                                                <Form.Group label="URL" >
                                                                    <Form.Input
                                                                        placeholder="https://domain.com"
                                                                        onChange={(e) => setFormElements({...formElements, url: e.target.value})}
                                                                        value={formElements.url}
                                                                        className="input-normal"
                                                                        feedback={errors?.url && errors?.url[0]}
                                                                        invalid={errors?.url}
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                        { user?.type === ROLE_ADMIN &&
                                                            <Grid.Row>
                                                                <Grid.Col md={ 6 } width={ 12 } >
                                                                    <Form.Group label="INTERNAL ID" >
                                                                        <Form.Input value={formElements.unique_id} className="input-normal" disabled />
                                                                    </Form.Group>
                                                                </Grid.Col>
                                                                <Grid.Col md={ 6 } width={ 12 } >
                                                                    <Form.Group label="DEALER" >
                                                                        <Form.Input
                                                                            placeholder="dealer"
                                                                            onChange={(e) => setFormElements({...formElements, dealer: e.target.value})}
                                                                            value={formElements.dealer}
                                                                            className="input-normal"
                                                                            feedback={errors?.dealer && errors?.dealer[0]}
                                                                            invalid={errors?.dealer}
                                                                        />
                                                                    </Form.Group>
                                                                </Grid.Col>
                                                            </Grid.Row>
                                                        }
                                                        { user?.type === ROLE_DEALER &&
                                                            <Grid.Row>
                                                                <Grid.Col md={ 6 } width={ 12 } >
                                                                    <Form.Group label="INTERNAL ID" >
                                                                        <Form.Input value={formElements.unique_id} className="input-normal" disabled />
                                                                    </Form.Group>
                                                                </Grid.Col>
                                                            </Grid.Row>
                                                        }
                                                    </div>

                                                    {/* Description & Features */}
                                                    <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 4px', marginBottom: 16 }}>
                                                        <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Description & Features</p>
                                                        <Grid.Row>
                                                            <Grid.Col width={ 12 } >
                                                                <Form.Group label="DESCRIPTION" >
                                                                    <Form.Textarea
                                                                        placeholder="Description"
                                                                        onChange={(e) => setFormElements({...formElements, description: e.target.value})}
                                                                        value={formElements.description}
                                                                        className="input-normal"
                                                                        feedback={errors?.description && errors?.description[0]}
                                                                        invalid={errors?.description}
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                            <Grid.Col width={ 12 } >
                                                                <Form.Group label="FEATURES" >
                                                                    <Form.Textarea
                                                                        placeholder="• Features"
                                                                        onChange={(e) => setFormElements({...formElements, features: e.target.value})}
                                                                        value={formElements.features}
                                                                        className="input-normal"
                                                                        feedback={errors?.features && errors?.features[0]}
                                                                        invalid={errors?.features}
                                                                    />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                    </div>

                                                    {/* Dimensions */}
                                                    <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 4px', marginBottom: 16 }}>
                                                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 12 }}>
                                                            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', margin: 0 }}>Dimensions</p>
                                                            <div className="d-flex align-items-center">
                                                                <span className="text-secondary mr-2" style={{ fontSize: 12 }}>cm</span>
                                                                <Form.Switch
                                                                    name="dimensionUnit"
                                                                    className="mx-2"
                                                                    checked={measureUnit === 'in'}
                                                                    onChange={(e) => setMeasureUnit(e.target.checked ? 'in' : 'cm')}
                                                                />
                                                                <span className="text-secondary" style={{ fontSize: 12 }}>in</span>
                                                            </div>
                                                        </div>
                                                        <Grid.Row>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label={`WIDTH (${measureUnit})`} >
                                                                    <Form.Input placeholder={measureUnit} type="number" onChange={(e) => setFormElements({...formElements, width: storeVal(e.target.value)})} value={displayVal(formElements.width)} className="input-normal" feedback={errors?.width && errors?.width[0]} invalid={errors?.width} />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label={`HEIGHT (${measureUnit})`} >
                                                                    <Form.Input placeholder={measureUnit} type="number" onChange={(e) => setFormElements({...formElements, height: storeVal(e.target.value)})} value={displayVal(formElements.height)} className="input-normal" feedback={errors?.height && errors?.height[0]} invalid={errors?.height} />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label={`DEPTH (${measureUnit})`} >
                                                                    <Form.Input placeholder={measureUnit} type="number" onChange={(e) => setFormElements({...formElements, depth: storeVal(e.target.value)})} value={displayVal(formElements.depth)} className="input-normal" feedback={errors?.depth && errors?.depth[0]} invalid={errors?.depth} />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                            <Grid.Col md={ 6 } width={ 12 } >
                                                                <Form.Group label="WEIGHT (optional)" >
                                                                    <Form.Input placeholder="kg" onChange={(e) => setFormElements({...formElements, weight: e.target.value})} value={formElements.weight ?? ''} className="input-normal" />
                                                                </Form.Group>
                                                            </Grid.Col>
                                                        </Grid.Row>
                                                    </div>

                                                </Grid.Col>
                                                <Grid.Col xl={ 6 } width={ 12 } >
                                                    { activeVariant === -1 ? (
                                                        <>
                                                        { variants.length > 0 && (
                                                            <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 4px', marginBottom: 16 }}>
                                                                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Default Variant</p>
                                                                <Form.Group label="DEFAULT COLOUR NAME">
                                                                    <Form.Input
                                                                        placeholder="e.g. Blue"
                                                                        value={formElements.default_label}
                                                                        onChange={(e) => setFormElements({...formElements, default_label: e.target.value})}
                                                                        className="input-normal"
                                                                    />
                                                                </Form.Group>
                                                            </div>
                                                        )}
                                                        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 8px', marginBottom: 16 }}>
                                                            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Images</p>
                                                            <div className="imag-tile-container">
                                                                {savedImages.map((url, index) => (
                                                                    <div className="wrapper" key={index}>
                                                                        <div className="image-tile"><img src={`${BACKENDURL}storage/${url}`} alt="" /></div>
                                                                        <Button type="button" color="secondary" onClick={() => DeleteSavedFile(index)}>Delete</Button>
                                                                    </div>
                                                                ))}
                                                                {selectedFiles.map((data, index) => {
                                                                    const { filename, fileimage } = data;
                                                                    return (
                                                                        <div className="wrapper" key={index}>
                                                                            {filename.match(/.(jpg|jpeg|png|gif|svg)$/i) ?
                                                                                <div className="image-tile"><img src={fileimage} alt="" /></div> :
                                                                                <div className="image-tile"><i className="far fa-file-alt"></i></div>
                                                                            }
                                                                            <Button type="button" color="secondary" onClick={() => DeleteSelectFile(index)}>Delete</Button>
                                                                        </div>
                                                                    );
                                                                })}
                                                                <div className="wrapper">
                                                                    <input type="file" ref={fileInputRef} className="hidden" onChange={InputChange} multiple accept=".png, .jpg, .jpeg" />
                                                                    <div className="image-tile add" onClick={() => fileInputRef?.current?.click()}>
                                                                        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M39.7511 2.5V77" stroke="white" strokeWidth="5" strokeLinecap="square"/>
                                                                            <path d="M77 39.7501H2.5" stroke="white" strokeWidth="5" strokeLinecap="square"/>
                                                                        </svg>
                                                                    </div>
                                                                    <Button type="button" color="secondary" onClick={() => fileInputRef?.current?.click()}>Add</Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {/* Variant details */}
                                                            <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 4px', marginBottom: 16 }}>
                                                                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Variant Details</p>
                                                                <Form.Group label="COLOUR NAME">
                                                                    <Form.Input
                                                                        placeholder="e.g. Midnight Black"
                                                                        value={variants[activeVariant]?.label || ''}
                                                                        onChange={(e) => updateVariant(activeVariant, 'label', e.target.value)}
                                                                        className="input-normal"
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group label="URL (optional — overrides base)">
                                                                    <Form.Input
                                                                        placeholder="https://domain.com/variant"
                                                                        value={variants[activeVariant]?.url || ''}
                                                                        onChange={(e) => updateVariant(activeVariant, 'url', e.target.value)}
                                                                        className="input-normal"
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group label="PRICE (optional — overrides base)">
                                                                    <Form.Input
                                                                        placeholder="Price"
                                                                        value={variants[activeVariant]?.price || ''}
                                                                        onChange={(e) => updateVariant(activeVariant, 'price', e.target.value)}
                                                                        className="input-normal"
                                                                    />
                                                                </Form.Group>
                                                            </div>

                                                            {/* Variant images */}
                                                            <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 8px', marginBottom: 16 }}>
                                                                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', marginBottom: 12 }}>Images</p>
                                                                <div className="imag-tile-container">
                                                                    {(variantImageStates[activeVariant]?.savedImages || []).map((url, index) => (
                                                                        <div className="wrapper" key={index}>
                                                                            <div className="image-tile"><img src={`${BACKENDURL}storage/${url}`} alt="" /></div>
                                                                            <Button type="button" color="secondary" onClick={() => {
                                                                                const vs = variantImageStates[activeVariant];
                                                                                updateVariantImageState(activeVariant, 'deletedImages', [...(vs.deletedImages || []), vs.savedImages[index]]);
                                                                                updateVariantImageState(activeVariant, 'savedImages', vs.savedImages.filter((_, i) => i !== index));
                                                                            }}>Delete</Button>
                                                                        </div>
                                                                    ))}
                                                                    {(variantImageStates[activeVariant]?.selectedFiles || []).map((data, index) => {
                                                                        const { filename, fileimage } = data;
                                                                        return (
                                                                            <div className="wrapper" key={index}>
                                                                                {filename.match(/.(jpg|jpeg|png|gif|svg)$/i) ?
                                                                                    <div className="image-tile"><img src={fileimage} alt="" /></div> :
                                                                                    <div className="image-tile"><i className="far fa-file-alt"></i></div>
                                                                                }
                                                                                <Button type="button" color="secondary" onClick={() => {
                                                                                    updateVariantImageState(activeVariant, 'selectedFiles', (variantImageStates[activeVariant]?.selectedFiles || []).filter((_, i) => i !== index));
                                                                                }}>Delete</Button>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    <div className="wrapper">
                                                                        <input
                                                                            type="file"
                                                                            ref={el => { variantFileRefs.current[activeVariant] = el; }}
                                                                            className="hidden"
                                                                            onChange={(e) => handleVariantFileInput(activeVariant, e)}
                                                                            multiple
                                                                            accept=".png, .jpg, .jpeg"
                                                                        />
                                                                        <div className="image-tile add" onClick={() => variantFileRefs.current[activeVariant]?.click()}>
                                                                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                <path d="M39.7511 2.5V77" stroke="white" strokeWidth="5" strokeLinecap="square"/>
                                                                                <path d="M77 39.7501H2.5" stroke="white" strokeWidth="5" strokeLinecap="square"/>
                                                                            </svg>
                                                                        </div>
                                                                        <Button type="button" color="secondary" onClick={() => variantFileRefs.current[activeVariant]?.click()}>Add</Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </Grid.Col>
                                            </Grid.Row>
                                            <Grid.Row>
                                                <Grid.Col width={ 12 }>
                                                    <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '16px 16px 8px', marginBottom: 16 }}>
                                                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 12 }}>
                                                            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#6c757d', textTransform: 'uppercase', margin: 0 }}>Recommendation Requirements</p>
                                                            <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                                                <div className="d-flex align-items-center">
                                                                    <span className="text-secondary mr-2" style={{ fontSize: 12 }}>cm</span>
                                                                    <Form.Switch
                                                                        name="measureUnit"
                                                                        className="mx-2"
                                                                        checked={measureUnit === 'in'}
                                                                        onChange={(e) => setMeasureUnit(e.target.checked ? 'in' : 'cm')}
                                                                    />
                                                                    <span className="text-secondary" style={{ fontSize: 12 }}>in</span>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <span className="text-secondary mr-2" style={{ fontSize: 12 }}>kg</span>
                                                                    <Form.Switch
                                                                        name="weightUnit"
                                                                        className="mx-2"
                                                                        checked={weightUnit === 'stlb'}
                                                                        onChange={(e) => setWeightUnit(e.target.checked ? 'stlb' : 'kg')}
                                                                    />
                                                                    <span className="text-secondary" style={{ fontSize: 12 }}>st / lb</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    {
                                                        [...measurements]?.map((measurement, index) => (
                                                            <Grid.Row key={index}>
                                                                <Grid.Col md={ 5 } width={ 12 } >
                                                                    <Form.Group label="MEASUREMENTS" >
                                                                        <Form.Select
                                                                            placeholder="Measurement"
                                                                            onChange={(e) => {
                                                                                const { value } = e.target;
                                                                                setMeasurements((prevMeasure) => {
                                                                                    const newMeasurements = [...prevMeasure];
                                                                                    newMeasurements[index] = {
                                                                                        ...newMeasurements[index],
                                                                                        measurement: value
                                                                                    };
                                                                                    return newMeasurements;
                                                                                });
                                                                            }}
                                                                            value={measurement.measurement}
                                                                            className="input-normal"
                                                                        >
                                                                            <option value="Height">Height</option>
                                                                            <option value="Weight">Weight (kg)</option>
                                                                            <option value="seatHeight">Seat Height</option>
                                                                            <option value="seatDepth">Seat Depth</option>
                                                                            <option value="floorToKnee">Floor to Knee</option>
                                                                            <option value="Hip Width (Outline)">Hip Width (Outline)</option>
                                                                            {tags.map((tag, i) => (
                                                                                <option key={i} value={tag.tag}>{tag.tag} (Tag)</option>
                                                                            ))}
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                </Grid.Col>
                                                                {measurement.measurement === 'Weight' && weightUnit === 'stlb' ? (<>
                                                                    <Grid.Col md={ 3 } width={ 12 } >
                                                                        <Form.Group label="MIN">
                                                                            <div className="d-flex" style={{ gap: 4 }}>
                                                                                <Form.Select
                                                                                    className="input-normal"
                                                                                    style={{ width: '50%' }}
                                                                                    value={kgToStLb(measurement.min).st}
                                                                                    onChange={(e) => {
                                                                                        const lb = kgToStLb(measurement.min).lb || 0;
                                                                                        setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], min: stLbToKg(e.target.value, lb) }; return n; });
                                                                                    }}
                                                                                >
                                                                                    {Array.from({ length: 31 }, (_, i) => <option key={i} value={String(i)}>{i}st</option>)}
                                                                                </Form.Select>
                                                                                <Form.Select
                                                                                    className="input-normal"
                                                                                    style={{ width: '50%' }}
                                                                                    value={kgToStLb(measurement.min).lb}
                                                                                    onChange={(e) => {
                                                                                        const st = kgToStLb(measurement.min).st || 0;
                                                                                        setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], min: stLbToKg(st, e.target.value) }; return n; });
                                                                                    }}
                                                                                >
                                                                                    {Array.from({ length: 14 }, (_, i) => <option key={i} value={String(i)}>{i}lb</option>)}
                                                                                </Form.Select>
                                                                            </div>
                                                                        </Form.Group>
                                                                    </Grid.Col>
                                                                    <Grid.Col md={ 3 } width={ 12 } >
                                                                        <Form.Group label="MAX">
                                                                            <div className="d-flex" style={{ gap: 4 }}>
                                                                                <Form.Select
                                                                                    className="input-normal"
                                                                                    style={{ width: '50%' }}
                                                                                    value={kgToStLb(measurement.max).st}
                                                                                    onChange={(e) => {
                                                                                        const lb = kgToStLb(measurement.max).lb || 0;
                                                                                        setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], max: stLbToKg(e.target.value, lb) }; return n; });
                                                                                    }}
                                                                                >
                                                                                    {Array.from({ length: 31 }, (_, i) => <option key={i} value={String(i)}>{i}st</option>)}
                                                                                </Form.Select>
                                                                                <Form.Select
                                                                                    className="input-normal"
                                                                                    style={{ width: '50%' }}
                                                                                    value={kgToStLb(measurement.max).lb}
                                                                                    onChange={(e) => {
                                                                                        const st = kgToStLb(measurement.max).st || 0;
                                                                                        setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], max: stLbToKg(st, e.target.value) }; return n; });
                                                                                    }}
                                                                                >
                                                                                    {Array.from({ length: 14 }, (_, i) => <option key={i} value={String(i)}>{i}lb</option>)}
                                                                                </Form.Select>
                                                                            </div>
                                                                        </Form.Group>
                                                                    </Grid.Col>
                                                                </>) : (<>
                                                                    <Grid.Col md={ 3 } width={ 12 } >
                                                                        <Form.Group label={`MIN (${measurement.measurement === 'Weight' ? 'kg' : measureUnit})`}>
                                                                            <Form.Input
                                                                                placeholder={measurement.measurement === 'Weight' ? 'kg' : measureUnit}
                                                                                type="number"
                                                                                onChange={(e) => {
                                                                                    const value = storeVal(e.target.value);
                                                                                    setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], min: measurement.measurement === 'Weight' ? e.target.value : value }; return n; });
                                                                                }}
                                                                                value={measurement.measurement === 'Weight' ? (measurement.min ?? '') : displayVal(measurement.min)}
                                                                                className="input-normal"
                                                                            />
                                                                        </Form.Group>
                                                                    </Grid.Col>
                                                                    <Grid.Col md={ 3 } width={ 12 } >
                                                                        <Form.Group label={`MAX (${measurement.measurement === 'Weight' ? 'kg' : measureUnit})`}>
                                                                            <Form.Input
                                                                                placeholder={measurement.measurement === 'Weight' ? 'kg' : measureUnit}
                                                                                type="number"
                                                                                onChange={(e) => {
                                                                                    const value = storeVal(e.target.value);
                                                                                    setMeasurements((prev) => { const n = [...prev]; n[index] = { ...n[index], max: measurement.measurement === 'Weight' ? e.target.value : value }; return n; });
                                                                                }}
                                                                                value={measurement.measurement === 'Weight' ? (measurement.max ?? '') : displayVal(measurement.max)}
                                                                                className="input-normal"
                                                                            />
                                                                        </Form.Group>
                                                                    </Grid.Col>
                                                                </>)}
                                                                <Grid.Col md={ 1 } width={ 12 } className="v-align" >
                                                                    <Button
                                                                        color="secondary"
                                                                        onClick={() => {
                                                                            setMeasurements((prev) => [
                                                                                ...prev.slice(0, index),
                                                                                ...prev.slice(index + 1)
                                                                            ]);
                                                                        }}
                                                                        className="btn-remove"
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Grid.Col>
                                                            </Grid.Row>
                                                        ))
                                                    }
                                                    <div className="line-button">
                                                        <Button
                                                            color="secondary"
                                                            onClick={() => {
                                                                setMeasurements((prev) => [...prev, {
                                                                    measurement: 'Height',
                                                                    min: '',
                                                                    max: ''
                                                                }])
                                                            }}
                                                        >
                                                            Add Measurement &nbsp;
                                                            <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M4.5 1V8" stroke="black" strokeWidth="2" strokeLinecap="square"/>
                                                                <path d="M8 4.5H1" stroke="black" strokeWidth="2" strokeLinecap="square"/>
                                                            </svg>
                                                        </Button>
                                                    </div>
                                                    </div>
                                                </Grid.Col>
                                            </Grid.Row>
                                        </Grid.Col>
                                    }
                                        <Grid.Col lg={2} width={12} ></Grid.Col>
                                    {
                                        saveSuccess && 
                                        <Grid.Col lg={8} width={12} >
                                            <Grid.Row>
                                                <h1 className="page-header-title">Save Success</h1>
                                            </Grid.Row>
                                            <Grid.Row>
                                                <label className="text-secondary">
                                                    {
                                                        id !== 0 ?
                                                        "Product successfully updated." : "New product successfully added."
                                                    }
                                                </label>
                                            </Grid.Row>
                                            <Grid.Row>
                                                <Button
                                                    color=" btn-mm d-flex justify-content-center align-items-center"
                                                    onClick={() => {
                                                        props.history.push("/products");
                                                    }}
                                                >
                                                    Back To Products
                                                </Button>
                                            </Grid.Row>
                                        </Grid.Col>
                                    }

                                    
                                </Grid.Row>

                            </Card.Body>
                            
                        
                        </Card>
                    </Grid.Col>
                </Grid.Row>
            </Page.Content>


            <Modal
                show={handleModalShow}
                centered={true}
                onHide={handleClose}
                dialogClassName="delete-user cancel-subscription"
            >
                <Modal.Body>
                    <div
                        style={{ textAlign: "right", cursor: "pointer" }}
                        className='close-icon'
                    >
                        <svg
                        width="21px"
                        height="20px"
                        viewBox="0 0 32 32"
                        onClick={handleClose}
                        >
                        <g
                            stroke="#E5E9EF"
                            strokeWidth="1"
                            fill="none"
                            fillRule="evenodd"
                        >
                            <g
                            transform="translate(-419.000000, -240.000000)"
                            fill="#E5E9EF"
                            >
                            <g
                                id="icons"
                                transform="translate(56.000000, 160.000000)"
                            >
                                <polygon points="375.0183 90 384 98.554 382.48065 100 373.5 91.446 364.5183 100 363 98.554 371.98065 90 363 81.446 364.5183 80 373.5 88.554 382.48065 80 384 81.446"></polygon>
                            </g>
                            </g>
                        </g>
                        </svg>
                    </div>
                    <div className="d-flex justify-content-center align-items-center notification-content">
                        <svg width="50px" height="50px" viewBox="0 0 50 50">
                        <g
                            id="Desktop-App---BRANDED-V2"
                            stroke="none"
                            strokeWidth="1"
                            fill="none"
                            fillRule="evenodd"
                        >
                            <g
                            id="Settings---Delete-User-Modal"
                            transform="translate(-695.000000, -376.000000)"
                            fill="#FF4747"
                            >
                            <g
                                id="Group-8"
                                transform="translate(530.000000, 336.000000)"
                            >
                                <g
                                id="Group-6"
                                transform="translate(165.000000, 40.000000)"
                                >
                                <rect
                                    id="Rectangle"
                                    fillOpacity="0.1"
                                    x="0"
                                    y="0"
                                    width="50"
                                    height="50"
                                    rx="5"
                                ></rect>
                                <g
                                    id="delete"
                                    transform="translate(15.000000, 15.000000)"
                                >
                                    <path
                                    d="M5,2 C5,0.89543 5.89543,0 7,0 L13,0 C14.1046,0 15,0.89543 15,2 L15,4 L16.9897,4 C16.9959,3.99994 17.0021,3.99994 17.0083,4 L19,4 C19.5523,4 20,4.44772 20,5 C20,5.55228 19.5523,6 19,6 L17.9311,6 L17.0638,18.1425 C16.989,19.1891 16.1182,20 15.0689,20 L4.93112,20 C3.88184,20 3.01096,19.1891 2.9362,18.1425 L2.06888,6 L1,6 C0.44772,6 0,5.55228 0,5 C0,4.44772 0.44772,4 1,4 L2.99174,4 C2.99795,3.99994 3.00414,3.99994 3.01032,4 L5,4 L5,2 Z M7,4 L13,4 L13,2 L7,2 L7,4 Z M4.07398,6 L4.93112,18 L15.0689,18 L15.926,6 L4.07398,6 Z M8,8 C8.5523,8 9,8.4477 9,9 L9,15 C9,15.5523 8.5523,16 8,16 C7.44772,16 7,15.5523 7,15 L7,9 C7,8.4477 7.44772,8 8,8 Z M12,8 C12.5523,8 13,8.4477 13,9 L13,15 C13,15.5523 12.5523,16 12,16 C11.4477,16 11,15.5523 11,15 L11,9 C11,8.4477 11.4477,8 12,8 Z"
                                    id="Shape"
                                    ></path>
                                </g>
                                </g>
                            </g>
                            </g>
                        </g>
                        </svg>
                        <div className="content-title">Delete Product?</div>
                        <div className="content-note">
                            Are you sure? This cannot be undone.
                        </div>
                        <div
                        className="d-flex user-page"
                        style={{ marginTop: 20 }}
                        >
                        <Button
                            outline
                            color="danger"
                            onClick={ deleteProduct }
                        >
                            Yes, Delete Product
                        </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </SiteWrapper>
    )
}

export default EditProduct;